import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { Search, Image as ImageIcon, Upload, Loader2 } from 'lucide-react';

import { Asset } from '../lib/api-types';
import { tenantApi } from '../lib/tenant-api';
import AppIconImage from './AppIconImage';
import { PageLoader } from './common';
import Modal from './Modal';

const ANY_IMAGE = 'image/*';

/** Whether an asset is one of the types in a file input's accept list, e.g. "image/*" or "image/png". */
const matchesAccept = (asset: Asset, accept: string) => {
  const mime = (asset.mime_type ?? '').toLowerCase();
  return accept.split(',').some((part) => {
    const type = part.trim().toLowerCase();
    if (type.startsWith('.')) return asset.file_name.toLowerCase().endsWith(type);
    if (type.endsWith('/*')) return mime.startsWith(type.slice(0, -1));
    return mime === type;
  });
};

/** "image/png" -> "PNG", "image/png,image/jpeg" -> "PNG or JPEG". */
const acceptLabel = (accept: string) =>
  accept
    .split(',')
    .map((part) => part.trim().replace(/^\./, '').replace(/^image\//i, '').toUpperCase())
    .join(' or ');

// ── Image Asset Picker ────────────────────────────────────────────────────────

export function ImageAssetPicker({ onSelect, onClose, accept = ANY_IMAGE, asAppIcons = false }: Readonly<{
  onSelect: (url: string) => void;
  onClose: () => void;
  /** Image types to list, as in a file input's accept. */
  accept?: string;
  /** Show each image as an app icon: its centre square, with the app's rounded corners (AppIconImage). */
  asAppIcons?: boolean;
}>) {
  const [assets, setAssets] = useState<{ id: string; name: string; url: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const narrowed = accept !== ANY_IMAGE;

  useEffect(() => {
    tenantApi.getAssets()
      .then(all => {
        setAssets(
          all
            .filter(a => a.asset_type === 'image' && matchesAccept(a, accept))
            .map(a => ({ id: a.id, name: a.file_name, url: a.custom_metadata.public_url }))
        );
      })
      .catch(() => { /* silently ignore */ })
      .finally(() => setLoading(false));
  }, [accept]);

  const filtered = assets.filter(a => a.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <Modal isOpen onClose={onClose} title="Select Image Asset" size="xl">
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search images..."
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            className="w-full pl-9 pr-4 py-2 border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        {narrowed && <p className="text-xs text-gray-500">Showing {acceptLabel(accept)} images only.</p>}
        {loading ? (
          <PageLoader fullScreen={false} />
        ) : filtered.length === 0 ? (
          <p className="text-center text-sm text-gray-500 py-8">
            {narrowed ? `No ${acceptLabel(accept)} images found in asset management.` : 'No images found in asset management.'}
          </p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-96 overflow-auto pr-1">
            {filtered.map(a => (
              <button
                key={a.id}
                type="button"
                onClick={() => onSelect(a.url)}
                className="group relative aspect-square border border-gray-200 overflow-hidden hover:border-indigo-400 hover:ring-2 hover:ring-indigo-300 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500"
                title={a.name}
              >
                {asAppIcons ? (
                  <AppIconImage src={a.url} alt={a.name} className="w-full h-full p-2" />
                ) : (
                  <img src={a.url} alt={a.name} className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-end p-1.5">
                  <span className="text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-1.5 py-0.5 truncate max-w-full">
                    {a.name}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Image URL input: paste a link, browse image assets, or upload one ─────────

/**
 * Uploads the file to Assets and reports its own errors. It hands the asset's public URL to
 * apply, which fills the field and returns true, or returns false and leaves the field alone
 * when its value changed while the upload ran.
 */
export type ImageUploader = (file: File, apply: (url: string) => boolean) => Promise<void>;

const uploadImageAsset: ImageUploader = async (file, apply) => {
  try {
    const asset = await tenantApi.uploadAsset(file);
    if (apply(asset.custom_metadata.public_url)) toast.success('Image uploaded and applied!');
  } catch (err) {
    toast.error(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
  }
};

interface ImageAssetUrlInputProps {
  id: string;
  value: string;
  onChange: (url: string) => void;
  disabled?: boolean;
  placeholder?: string;
  maxLength?: number;
  /** Image types Browse lists and Upload offers. */
  accept?: string;
  /** Replaces the default upload (any image, toast on success or failure). */
  uploadFile?: ImageUploader;
  /** The Upload button's text while uploadFile runs. */
  uploadingLabel?: string;
  /** Browse shows each image as an app icon (see ImageAssetPicker). */
  asAppIcons?: boolean;
}

export default function ImageAssetUrlInput({
  id,
  value,
  onChange,
  disabled,
  placeholder,
  maxLength,
  accept = ANY_IMAGE,
  uploadFile = uploadImageAsset,
  uploadingLabel = 'Uploading…',
  asAppIcons = false,
}: Readonly<ImageAssetUrlInputProps>) {
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Counts value changes, so an upload that finishes after the admin typed, browsed or cleared
  // the field leaves their newer choice in place.
  const valueVersion = useRef(0);
  useEffect(() => {
    valueVersion.current += 1;
  }, [value]);

  const handleAssetUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const startVersion = valueVersion.current;
    setIsUploading(true);
    try {
      await uploadFile(file, (url) => {
        if (valueVersion.current !== startVersion) return false;
        onChange(url);
        return true;
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <>
      <div className="flex gap-1.5">
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          maxLength={maxLength}
          className="flex-1 min-w-0 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-50 disabled:text-gray-400"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleAssetUpload}
        />
        <button
          type="button"
          onClick={() => setShowAssetPicker(true)}
          disabled={disabled}
          title="Browse image assets"
          className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-2 border border-dashed border-indigo-300 text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-40 text-xs font-medium"
        >
          <ImageIcon className="h-3.5 w-3.5" />
          Browse
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isUploading}
          title="Upload new image asset"
          className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-2 border border-dashed border-emerald-400 text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-40 text-xs font-medium"
        >
          {isUploading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Upload className="h-3.5 w-3.5" />
          }
          {isUploading ? uploadingLabel : 'Upload'}
        </button>
      </div>
      {showAssetPicker && (
        <ImageAssetPicker
          accept={accept}
          asAppIcons={asAppIcons}
          onSelect={(url) => { onChange(url); setShowAssetPicker(false); }}
          onClose={() => setShowAssetPicker(false)}
        />
      )}
    </>
  );
}
